import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { DeliverableCard } from '../components/DeliverableCard'

interface Evidence {
  id: string
  title: string
  evidence_type: 'file' | 'link' | 'commit' | 'activity'
  path: string | null
  relationship: 'contributes' | 'completes'
  status: 'suggested' | 'confirmed' | 'rejected'
  added_by: { id: string; name: string } | null
  added_at: string | null
  deliverable_id: string | null
}

interface Deliverable {
  id: string
  goal_id: string
  title: string
  status: 'not_started' | 'in_progress' | 'complete'
  due_date: string | null
  start_date: string | null
  verification: string | null
  evidence?: Evidence[]
  assignee?: { id: string; name: string } | null
}

interface Goal {
  id: string
  title: string
  description: string | null
  status: 'not_started' | 'in_progress' | 'at_risk' | 'complete'
  due_date: string | null
  owner?: { id: string; name: string }
  assignee?: { id: string; name: string }
  project?: { id: string; name: string; project_id: string } | null
  deliverables?: Deliverable[]
}

interface Person {
  id: string
  name: string
  email: string
  title: string | null
  role: string | null
}

interface ActivityEvent {
  timestamp: string
  event_type: string
  title: string
  description: string
  author_name: string
  author_email: string
  source_name: string
  source_type: string
  artifact_path: string
  url: string
  project_id: string
  project_name: string
}

interface ActivityResponse {
  events: ActivityEvent[]
  projects: { id: string; name: string }[]
}


const statusColors: Record<string, string> = {
  not_started: 'bg-gray-800 text-gray-300',
  in_progress: 'bg-blue-900/50 text-blue-300',
  at_risk: 'bg-red-900/50 text-red-300',
  complete: 'bg-green-900/50 text-green-300',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

function getDaysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null
  const due = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  due.setHours(0, 0, 0, 0)
  return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

function getDueDateBadge(dateStr: string | null, status: string) {
  if (status === 'complete') return null
  const days = getDaysUntil(dateStr)
  if (days === null) return null

  if (days < 0) {
    return <span className="text-xs px-1.5 py-0.5 bg-red-900/50 text-red-300 rounded">{Math.abs(days)}d overdue</span>
  } else if (days === 0) {
    return <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-300 rounded">Due today</span>
  } else if (days <= 7) {
    return <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-300 rounded">{days}d left</span>
  }
  return null
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export function MyWork() {
  const [showCompleted, setShowCompleted] = useState(false)
  const queryClient = useQueryClient()

  // Get current person's info
  const { data: me } = useQuery({
    queryKey: ['people', 'me'],
    queryFn: () => api.get<Person>('/people/me'),
  })

  // Get goals with deliverables and evidence
  const { data: goals, isLoading: goalsLoading } = useQuery({
    queryKey: ['people', 'me', 'goals', 'with-evidence'],
    queryFn: () => api.get<Goal[]>('/people/me/goals?include_deliverables=true&include_evidence=true'),
  })

  // Get recent activity
  const { data: activityData } = useQuery({
    queryKey: ['me', 'activity'],
    queryFn: () => api.get<ActivityResponse>('/people/me/activity?limit=20&days=14'),
  })

  if (goalsLoading) {
    return (
      <div className="max-w-4xl">
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </div>
    )
  }

  // Separate active and completed goals
  const activeGoals = goals?.filter(g => g.status !== 'complete') || []
  const completedGoals = goals?.filter(g => g.status === 'complete') || []

  // Calculate stats
  const totalDeliverables = goals?.reduce((sum, g) => sum + (g.deliverables?.length || 0), 0) || 0
  const completedDeliverables = goals?.reduce(
    (sum, g) => sum + (g.deliverables?.filter(d => d.status === 'complete').length || 0),
    0
  ) || 0

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl">My Work</h1>
          {me && (
            <p className="text-[var(--color-text-secondary)] mt-1">
              {me.title || me.role || me.email}
            </p>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="card text-center">
          <div className="text-3xl font-medium">{activeGoals.length}</div>
          <div className="text-sm text-[var(--color-text-secondary)]">Active Goals</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-medium">{completedDeliverables}/{totalDeliverables}</div>
          <div className="text-sm text-[var(--color-text-secondary)]">Deliverables</div>
        </div>
      </div>

      {/* Recent Activity */}
      {activityData?.events && activityData.events.length > 0 && (
        <section className="mb-8">
          <h2 className="section-label">Recent Activity</h2>
          <div className="card">
            <div className="space-y-2">
              {activityData.events.slice(0, 10).map((event, idx) => (
                <div
                  key={`${event.timestamp}-${idx}`}
                  className="flex items-center justify-between bg-[var(--color-surface)] p-3 rounded"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--color-bg)]">
                        {event.event_type}
                      </span>
                      <span className="text-sm font-medium truncate">{event.title}</span>
                    </div>
                    <div className="flex gap-3 text-xs text-[var(--color-text-secondary)] mt-1">
                      <span>{event.project_name}</span>
                      <span>{event.source_name}</span>
                      <span>{formatRelativeTime(event.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Active Goals */}
      <section className="mb-8">
        <h2 className="section-label">Active Goals</h2>
        {activeGoals.length > 0 ? (
          <div className="space-y-6">
            {activeGoals.map((goal) => (
              <div key={goal.id} className="card">
                {/* Goal Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{goal.title}</h3>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${statusColors[goal.status]}`}>
                        {goal.status.replace('_', ' ')}
                      </span>
                      {getDueDateBadge(goal.due_date, goal.status)}
                    </div>
                    {goal.description && (
                      <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                        {goal.description}
                      </p>
                    )}
                    <div className="flex gap-4 text-xs text-[var(--color-text-secondary)] mt-2">
                      {goal.due_date && <span>Due: {formatDate(goal.due_date)}</span>}
                      {goal.project && <span>Project: {goal.project.name}</span>}
                      {goal.owner && <span>Owner: {goal.owner.name}</span>}
                    </div>
                  </div>
                </div>

                {/* Deliverables */}
                {goal.deliverables && goal.deliverables.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-[var(--color-border)]">
                    <h4 className="text-xs font-medium text-[var(--color-text-secondary)] uppercase">
                      Deliverables
                    </h4>
                    {goal.deliverables.map((d) => (
                      <DeliverableCard
                        key={d.id}
                        deliverable={d}
                        type="personal"
                        onUpdate={() => queryClient.invalidateQueries({ queryKey: ['people', 'me', 'goals'] })}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="card">
            <p className="text-[var(--color-text-secondary)]">
              No active goals. Contact your manager to set up your goals.
            </p>
          </div>
        )}
      </section>

      {/* Completed Goals (collapsible) */}
      {completedGoals.length > 0 && (
        <section>
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="section-label text-[var(--color-text-secondary)] hover:text-[var(--color-text)] cursor-pointer"
          >
            {showCompleted ? '▼' : '▶'} Completed ({completedGoals.length})
          </button>
          {showCompleted && (
            <div className="space-y-4 mt-4">
              {completedGoals.map((goal) => (
                <div key={goal.id} className="card opacity-75">
                  <div className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    <h3 className="font-medium">{goal.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  )
}
